import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet, X, Loader2, Sparkles } from 'lucide-react';
import secureStorage from '../../utils/secureStorage';
import axios from 'axios';
import { getLocalDateString } from "../../lib/utils";
import { downloadFileWithProgress } from '../../lib/downloadProgress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExportButton = ({ 
  dataType, 
  filterType = 'all', 
  filterId = null,
  buttonText = 'Export',
  className = ''
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [formatType, setFormatType] = useState('formal');

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('token');
      const params = new URLSearchParams({
        format: exportFormat,
        format_type: formatType,
        filter_type: filterType,
      });
      
      if (filterId) {
        params.append('filter_id', filterId);
      }

      const response = await downloadFileWithProgress({
        label: `Laporan ${dataType} (${exportFormat === 'excel' ? 'Excel' : 'PDF'})`,
        filename: `laporan_${dataType}_${getLocalDateString()}.${exportFormat === 'excel' ? 'xlsx' : 'pdf'}`,
        request: (onDownloadProgress) => axios.get(
          `${API_URL}/api/export/${dataType}?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
            onDownloadProgress
          }
        )
      });
      setShowModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Gagal export laporan. Sila cuba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        data-testid={`export-button-${dataType}`}
        className={`flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl hover:from-emerald-600 hover:to-green-600 transition-all shadow-lg hover:shadow-xl font-semibold ${className}`}
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">{buttonText}</span>
      </button>

      {/* Export Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Download className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Export Laporan</h3>
                    <p className="text-sm text-white/80">Pilih format yang dikehendaki</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  data-testid="close-export-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Format Fail
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setExportFormat('pdf')}
                    data-testid="select-pdf-format"
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                      exportFormat === 'pdf'
                        ? 'border-red-500 bg-red-50 shadow-lg shadow-red-100'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <FileText className={`w-8 h-8 ${exportFormat === 'pdf' ? 'text-red-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold ${exportFormat === 'pdf' ? 'text-red-700' : 'text-gray-500'}`}>PDF</span>
                  </button>
                  <button
                    onClick={() => setExportFormat('excel')}
                    data-testid="select-excel-format"
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                      exportFormat === 'excel'
                        ? 'border-green-500 bg-green-50 shadow-lg shadow-green-100'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <FileSpreadsheet className={`w-8 h-8 ${exportFormat === 'excel' ? 'text-green-500' : 'text-gray-400'}`} />
                    <span className={`font-semibold ${exportFormat === 'excel' ? 'text-green-700' : 'text-gray-500'}`}>Excel</span>
                  </button>
                </div>
              </div>

              {/* PDF Format Type */}
              {exportFormat === 'pdf' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Jenis PDF</label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setFormatType('simple')}
                      data-testid="select-simple-format"
                      className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                        formatType === 'simple'
                          ? 'border-pink-500 bg-pink-50 shadow-lg shadow-pink-100'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`font-bold ${formatType === 'simple' ? 'text-pink-700' : 'text-gray-700'}`}>Simple Table</span>
                      <span className="text-xs text-gray-500 block mt-1">Format jadual ringkas tanpa hiasan</span>
                    </button>
                    <button
                      onClick={() => setFormatType('formal')}
                      data-testid="select-formal-format"
                      className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                        formatType === 'formal'
                          ? 'border-purple-500 bg-purple-50 shadow-lg shadow-purple-100'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`font-bold ${formatType === 'formal' ? 'text-purple-700' : 'text-gray-700'}`}>Formal Report</span>
                      <span className="text-xs text-gray-500 block mt-1">Dengan header & logo sekolah</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Export Button */}
              <button
                onClick={handleExport}
                disabled={loading}
                data-testid="confirm-export-btn"
                className={`w-full py-4 rounded-2xl font-bold text-white transition-all flex items-center justify-center gap-3 text-lg ${
                  loading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Sedang Export...
                  </>
                ) : (
                  <>
                    <Download className="w-6 h-6" />
                    Export Sekarang
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportButton;
